<template>
  <div class="data-stats-page">
    <header class="page-hero">
      <div>
        <span class="eyebrow">Data Overview</span>
        <h2>数据统计</h2>
        <p>统计全站用户积分余额、充值收入、成功消费、失败退款与分成，快速判断近期资金净额。</p>
      </div>
      <div class="hero-actions">
        <el-segmented v-model="rangeDays" :options="rangeOptions" class="range-segment" @change="getStats" />
        <el-button class="ghost-btn" :loading="loading" @click="getStats">刷新数据</el-button>
      </div>
    </header>

    <section class="stats-grid">
      <article class="stat-card primary">
        <span>用户剩余积分资产</span>
        <strong>{{ formatNumber(stats.totalPoints) }}</strong>
        <em>折合人民币 ￥{{ formatMoney(stats.totalRmb) }}，按 1 元 = 100 积分统计</em>
      </article>
      <article class="stat-card">
        <span>今日使用与成本</span>
        <strong>{{ formatNumber(stats.todayConsumePoints) }}</strong>
        <em>语言 ￥{{ formatMoney(stats.todayLanguageConsumeAmount) }} / 其它 ￥{{ formatMoney(stats.todayOtherConsumeAmount)
          }}</em>
        <em>成本 ￥{{ formatMoney(stats.todayModelCostAmount) }} / 分成 ￥{{ formatMoney(todayTotalRewardAmount) }}</em>
        <em :class="{ profit: todayProfitAmount > 0, loss: todayProfitAmount < 0 }">今日利润 ￥{{ formatMoney(todayProfitAmount) }}</em>
      </article>
      <article class="stat-card">
        <span>近 {{ rangeDays }} 日消耗统计</span>
        <strong>{{ formatNumber(stats.rangeConsumePoints) }}</strong>
        <em>成功 {{ formatNumber(stats.rangeConsumePoints / 100) }} / 失败 {{
          formatNumber(stats.rangeFailedConsumePoints / 100)
          }}</em>
      </article>
      <article class="stat-card">
        <span>近 {{ rangeDays }} 日语言模型扣除</span>
        <strong>{{ formatNumber(stats.rangeLanguageConsumePoints) }}</strong>
        <em>折合 ￥{{ formatMoney(stats.rangeLanguageConsumeAmount) }}，不重复计入其它模型</em>
      </article>

      <article class="stat-card">
        <span>近 {{ rangeDays }} 日资金净额</span>
        <strong :class="{ loss: rangeProfitAmount < 0 }">￥{{ formatMoney(rangeProfitAmount) }}</strong>
        <em>充值 ￥{{ formatMoney(rangeRechargeAmount) }} / 消费 ￥{{ formatMoney(rangeConsumeAmount) }} (含语言模型)</em>
      </article>
      <article class="stat-card">
        <span>近 {{ rangeDays }} 日经营利润</span>
        <strong :class="{ loss: rangeOperatingProfitAmount < 0 }">￥{{ formatMoney(rangeOperatingProfitAmount)
        }}</strong>
        <em>使用 ￥{{ formatMoney(rangeConsumeAmount) }} / 成本 ￥{{ formatMoney(stats.rangeModelCostAmount) }} / 分成 ￥{{
          formatMoney(rangeTotalRewardAmount) }}</em>
      </article>
    </section>

    <section class="chart-panel">
      <div class="panel-head">
        <div>
          <h3>近 {{ rangeDays }} 日充值、消费、模型成本和分成走势图</h3>
          <p>资金净额按充值收入 - 成功消费计算；每日利润按使用量 - 模型成本 - 总分成计算。</p>
        </div>
        <div class="chart-meta">
          <span>{{ chartRangeText }}</span>
          <div class="legend">
            <i class="recharge"></i>现金收入
            <i class="consume"></i>使用量
            <i class="cost"></i>模型成本
            <i class="agent"></i>总分成
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading-box">
        <el-skeleton :rows="6" animated />
      </div>

      <div v-else-if="!stats.dailyStats.length" class="empty-chart">
        暂无统计数据，请先补充后端接口。
      </div>

      <div v-else class="trend-chart">
        <div v-for="item in stats.dailyStats" :key="item.date" class="trend-item">
          <div class="trend-track">
            <div class="trend-line recharge" :title="getTrendTooltip(item, 'recharge')"
              :style="{ height: getTrendHeight(item.rechargePoints) }"></div>
            <div class="trend-line consume" :title="getTrendTooltip(item, 'consume')"
              :style="{ height: getTrendHeight(item.consumePoints) }"></div>
            <div class="trend-line cost" :title="getTrendTooltip(item, 'cost')"
              :style="{ height: getTrendHeight(item.modelCostPoints) }"></div>
            <div class="trend-line agent" :title="getTrendTooltip(item, 'agent')"
              :style="{ height: getTrendHeight(item.totalRewardPoints) }"></div>
          </div>
          <strong>{{ formatShortDate(item.date) }}</strong>
          <span>
            <b class="recharge">收入 ￥{{ formatMoney(item.rechargeAmount) }}</b>
            <b class="consume">使用 ￥{{ formatMoney(item.consumeAmount) }}</b>
            <b class="cost">成本 ￥{{ formatMoney(item.modelCostAmount) }}</b>
            <b class="agent">分成 ￥{{ formatMoney(item.totalRewardAmount) }}</b>
          </span>
          <em :class="{ loss: item.netAmount < 0 }">
            {{ item.netAmount >= 0 ? '+' : '' }}￥{{ formatMoney(item.netAmount) }}
          </em>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'

const loading = ref(false)
const rangeDays = ref(7)
const rangeOptions = [
  { label: '7日', value: 7 },
  { label: '30日', value: 30 },
  { label: '60日', value: 60 },
  { label: '90日', value: 90 },
]

const stats = reactive({
  totalPoints: 0,
  totalRmb: 0,
  todayConsumePoints: 0,
  todayConsumeAmount: 0,
  todayRechargePoints: 0,
  todayRechargeAmount: 0,
  todayLanguageConsumePoints: 0,
  todayLanguageConsumeAmount: 0,
  todayOtherConsumePoints: 0,
  todayOtherConsumeAmount: 0,
  todayModelCostPoints: 0,
  todayModelCostAmount: 0,
  todayAgentRewardPoints: 0,
  todayAgentRewardAmount: 0,
  todayTotalRewardPoints: 0,
  todayTotalRewardAmount: 0,
  rangeRechargeAmount: 0,
  rangeConsumePoints: 0,
  rangeConsumeAmount: 0,
  rangeLanguageConsumePoints: 0,
  rangeLanguageConsumeAmount: 0,
  rangeOtherConsumePoints: 0,
  rangeOtherConsumeAmount: 0,
  rangeFailedConsumePoints: 0,
  rangeFailedConsumeAmount: 0,
  rangeModelCostPoints: 0,
  rangeModelCostAmount: 0,
  rangeAgentRewardPoints: 0,
  rangeAgentRewardAmount: 0,
  rangePromotionRewardPoints: 0,
  rangePromotionRewardAmount: 0,
  rangeTotalRewardPoints: 0,
  rangeTotalRewardAmount: 0,
  rangeProfitAmount: 0,
  dailyStats: []
})

const rangeRechargeAmount = computed(() => Number(stats.rangeRechargeAmount || 0))
const rangeConsumeAmount = computed(() => Number(stats.rangeConsumeAmount || 0))
const rangeTotalRewardAmount = computed(() => Number(stats.rangeTotalRewardAmount || stats.rangeAgentRewardAmount || 0))
const todayTotalRewardAmount = computed(() => Number(
  stats.todayTotalRewardAmount ?? stats.todayAgentRewardAmount ?? 0
))
const todayProfitAmount = computed(() => Number((
  Number(stats.todayConsumeAmount || 0)
  - Number(stats.todayModelCostAmount || 0)
  - todayTotalRewardAmount.value
).toFixed(2)))
const rangeProfitAmount = computed(() => Number((
  rangeRechargeAmount.value - rangeConsumeAmount.value
).toFixed(2)))
const rangeOperatingProfitAmount = computed(() => Number((
  stats.dailyStats.reduce((total, item) => total + Number(item.profitAmount ?? item.netAmount ?? 0), 0)
).toFixed(2)))
const maxTrendPoints = computed(() => Math.max(
  ...stats.dailyStats.map(item => Math.max(
    Number(item.rechargePoints || 0),
    Number(item.consumePoints || 0),
    Number(item.modelCostPoints || 0),
    Number(item.totalRewardPoints || item.agentRewardPoints || 0)
  )),
  1
))
const chartRangeText = computed(() => {
  if (!stats.dailyStats.length) return `最近 ${rangeDays.value} 天`
  return `${stats.dailyStats[0].date} 至 ${stats.dailyStats[stats.dailyStats.length - 1].date}`
})

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

const getStats = async () => {
  loading.value = true
  try {
    const params = { days: rangeDays.value }
    const summaryRes = await axios.get('/admin/getDataStatsSummary', { params })

    if (summaryRes.data?.msg !== 'success') {
      ElMessage.error(summaryRes.data?.message || '获取统计摘要失败')
      return
    }

    applySummaryData(summaryRes.data.data || {})
    await delay(800)

    const dailyRes = await axios.get('/admin/getDataStatsDaily', { params })

    if (dailyRes.data?.msg !== 'success') {
      ElMessage.error(dailyRes.data?.message || '获取趋势数据失败')
      return
    }

    stats.dailyStats = normalizeDailyStats(dailyRes.data.data || {})
  } catch (error) {
    ElMessage.error('统计接口暂未接入或请求失败')
  } finally {
    loading.value = false
  }
}

const applySummaryData = (data) => {
  stats.totalPoints = Number(data.totalPoints || 0)
  stats.totalRmb = Number(data.totalRmb ?? stats.totalPoints / 100)
  stats.todayConsumePoints = Number(data.todayConsumePoints ?? data.todayCost ?? 0)
  stats.todayConsumeAmount = Number(data.todayConsumeAmount ?? stats.todayConsumePoints / 100)
  stats.todayRechargePoints = Number(data.todayRechargePoints || 0)
  stats.todayRechargeAmount = Number(data.todayRechargeAmount || 0)
  stats.todayLanguageConsumePoints = Number(data.todayLanguageConsumePoints || 0)
  stats.todayLanguageConsumeAmount = Number(data.todayLanguageConsumeAmount || 0)
  stats.todayOtherConsumePoints = Number(data.todayOtherConsumePoints || 0)
  stats.todayOtherConsumeAmount = Number(data.todayOtherConsumeAmount || 0)
  stats.todayModelCostPoints = Number(data.todayModelCostPoints || 0)
  stats.todayModelCostAmount = Number(data.todayModelCostAmount || 0)
  stats.todayAgentRewardPoints = Number(data.todayAgentRewardPoints || 0)
  stats.todayAgentRewardAmount = Number(data.todayAgentRewardAmount || 0)
  stats.todayTotalRewardPoints = Number(data.todayTotalRewardPoints || data.todayAgentRewardPoints || 0)
  stats.todayTotalRewardAmount = Number(data.todayTotalRewardAmount || data.todayAgentRewardAmount || 0)
  stats.rangeRechargeAmount = Number(data.rangeRechargeAmount || 0)
  stats.rangeConsumePoints = Number(data.rangeConsumePoints || data.rangeConsumeAmount * 100 || 0)
  stats.rangeConsumeAmount = Number(data.rangeConsumeAmount || 0)
  stats.rangeLanguageConsumePoints = Number(data.rangeLanguageConsumePoints || 0)
  stats.rangeLanguageConsumeAmount = Number(data.rangeLanguageConsumeAmount || 0)
  stats.rangeOtherConsumePoints = Number(data.rangeOtherConsumePoints || 0)
  stats.rangeOtherConsumeAmount = Number(data.rangeOtherConsumeAmount || 0)
  stats.rangeFailedConsumePoints = Number(data.rangeFailedConsumePoints || 0)
  stats.rangeFailedConsumeAmount = Number(data.rangeFailedConsumeAmount || 0)
  stats.rangeModelCostPoints = Number(data.rangeModelCostPoints || 0)
  stats.rangeModelCostAmount = Number(data.rangeModelCostAmount || 0)
  stats.rangeAgentRewardPoints = Number(data.rangeAgentRewardPoints || 0)
  stats.rangeAgentRewardAmount = Number(data.rangeAgentRewardAmount || 0)
  stats.rangePromotionRewardPoints = Number(data.rangePromotionRewardPoints || 0)
  stats.rangePromotionRewardAmount = Number(data.rangePromotionRewardAmount || 0)
  stats.rangeTotalRewardPoints = Number(data.rangeTotalRewardPoints || data.rangeAgentRewardPoints || 0)
  stats.rangeTotalRewardAmount = Number(data.rangeTotalRewardAmount || data.rangeAgentRewardAmount || 0)
  stats.rangeProfitAmount = Number(data.rangeProfitAmount ?? rangeProfitAmount.value)
}

const normalizeDailyStats = (data) => {
  if (Array.isArray(data.dailyStats)) return data.dailyStats.map(normalizeDailyItem)
  if (Array.isArray(data.dailyCost)) {
    return data.dailyCost.map(item => normalizeDailyItem({
      date: item.date,
      consumePoints: item.points,
      consumeAmount: item.amount,
      rechargePoints: 0,
      rechargeAmount: 0,
      languageConsumePoints: 0,
      languageConsumeAmount: 0,
      otherConsumePoints: 0,
      otherConsumeAmount: 0,
      modelCostPoints: 0,
      modelCostAmount: 0,
      agentRewardPoints: 0,
      agentRewardAmount: 0,
      promotionRewardPoints: 0,
      promotionRewardAmount: 0,
      totalRewardPoints: 0,
      totalRewardAmount: 0,
      profitAmount: -Number(item.amount || Number(item.points || 0) / 100)
    }))
  }
  return []
}

const normalizeDailyItem = (item) => {
  const rechargeAmount = Number(item.rechargeAmount || 0)
  const consumeAmount = Number(item.consumeAmount || item.amount || 0)
  const modelCostAmount = Number(item.modelCostAmount || 0)
  const totalRewardAmount = Number(item.totalRewardAmount || item.agentRewardAmount || 0)
  const netAmount = Number((consumeAmount - modelCostAmount - totalRewardAmount).toFixed(2))

  return {
    date: item.date,
    rechargePoints: Number(item.rechargePoints || 0),
    rechargeAmount,
    consumePoints: Number(item.consumePoints || item.points || 0),
    consumeAmount,
    languageConsumePoints: Number(item.languageConsumePoints || 0),
    languageConsumeAmount: Number(item.languageConsumeAmount || 0),
    otherConsumePoints: Number(item.otherConsumePoints || 0),
    otherConsumeAmount: Number(item.otherConsumeAmount || 0),
    modelCostPoints: Number(item.modelCostPoints || 0),
    modelCostAmount,
    agentRewardPoints: Number(item.agentRewardPoints || 0),
    agentRewardAmount: Number(item.agentRewardAmount || 0),
    promotionRewardPoints: Number(item.promotionRewardPoints || 0),
    promotionRewardAmount: Number(item.promotionRewardAmount || 0),
    totalRewardPoints: Number(item.totalRewardPoints || item.agentRewardPoints || 0),
    totalRewardAmount,
    profitAmount: netAmount,
    netAmount
  }
}

const getTrendHeight = (points) => {
  const percent = Math.max(4, Math.round((Number(points || 0) / maxTrendPoints.value) * 100))
  return `${percent}%`
}

const getTrendTooltip = (item, type) => {
  const map = {
    recharge: {
      label: '现金收入',
      amount: item.rechargeAmount,
      points: item.rechargePoints
    },
    consume: {
      label: '用户使用量',
      amount: item.consumeAmount,
      points: item.consumePoints
    },
    cost: {
      label: '模型成本',
      amount: item.modelCostAmount,
      points: item.modelCostPoints
    },
    agent: {
      label: '总分成',
      amount: item.totalRewardAmount,
      points: item.totalRewardPoints
    }
  }
  const data = map[type] || map.consume
  return `${item.date} ${data.label}\n金额：￥${formatMoney(data.amount)}\n积分：${formatNumber(data.points)}`
}

const formatNumber = (value) => Number(value || 0).toLocaleString('zh-CN', { maximumFractionDigits: 2 })
const formatMoney = (value) => Number(value || 0).toLocaleString('zh-CN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const formatShortDate = (date) => String(date || '').slice(5) || '-'

onMounted(() => {
  getStats()
})
</script>

<style scoped>
.data-stats-page {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
  padding: 24px;
  background: #070b12;
  color: #e8edf7;
  display: flex;
  flex-direction: column;
}

:global(.el-main:has(.data-stats-page)) {
  height: calc(100vh - 60px);
  padding: 0;
  overflow: hidden;
  background: #070b12;
}

.page-hero {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-end;
  padding: 24px;
  border: 1px solid #202938;
  border-radius: 10px;
  background: linear-gradient(135deg, #111827 0%, #0b111d 58%, #11131d 100%);
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.28);
  margin-bottom: 18px;
}

.page-hero h2 {
  margin: 6px 0 8px;
  color: #ffffff;
  font-size: 28px;
  line-height: 1.15;
}

.page-hero p {
  margin: 0;
  color: #8f9bad;
}

.eyebrow {
  color: #ff9b4a;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.stats-grid {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.stat-card,
.chart-panel {
  border: 1px solid #202938;
  border-radius: 10px;
  background: #0d131f;
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.24);
}

.stat-card {
  min-height: 128px;
  padding: 18px;
  box-sizing: border-box;
}

.stat-card.primary {
  border-color: rgba(255, 116, 23, 0.35);
  background: linear-gradient(135deg, rgba(255, 116, 23, 0.22), #101827 65%);
}

.stat-card span,
.stat-card em {
  display: block;
  color: #8f9bad;
  font-style: normal;
  font-size: 13px;
}

.stat-card strong {
  display: block;
  margin: 12px 0 10px;
  color: #ffffff;
  font-size: 28px;
  line-height: 1.15;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.range-segment {
  --el-segmented-bg-color: #151d2b;
  --el-segmented-item-selected-bg-color: rgba(255, 116, 23, 0.18);
  --el-segmented-item-selected-color: #ff9b4a;
  --el-segmented-item-hover-bg-color: rgba(255, 255, 255, 0.05);
  --el-border-radius-base: 6px;
}

.loss {
  color: #f87171 !important;
}

.profit {
  color: #22c55e !important;
}

.chart-panel {
  flex: 1;
  min-height: 0;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.panel-head {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.panel-head h3 {
  margin: 0 0 6px;
  color: #f5f7fb;
  font-size: 18px;
}

.panel-head p,
.panel-head span {
  margin: 0;
  color: #8f9bad;
  font-size: 13px;
}

.chart-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.legend {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #8f9bad;
  font-size: 12px;
}

.legend i {
  display: inline-block;
  width: 18px;
  height: 3px;
  margin-right: 4px;
  border-radius: 999px;
  vertical-align: middle;
}

.legend .recharge {
  background: #38bdf8;
}

.legend .consume {
  background: #ff7417;
}

.legend .cost {
  background: #f87171;
}

.legend .agent {
  background: #a78bfa;
}

.trend-chart {
  flex: 1;
  min-height: 260px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(34px, 1fr));
  gap: 10px;
  align-items: end;
}

.trend-item {
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
}

.trend-track {
  position: relative;
  flex: 1;
  width: 100%;
  min-height: 180px;
  border: 1px solid #202938;
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px) 0 0 / 100% 25%,
    #101827;
  overflow: hidden;
}

.trend-line {
  position: absolute;
  bottom: 0;
  width: 21%;
  min-height: 6px;
  border-radius: 6px 6px 0 0;
  transition: height 0.2s ease;
}

.trend-line.recharge {
  left: 6%;
  background: linear-gradient(180deg, #67e8f9 0%, #0284c7 100%);
}

.trend-line.consume {
  left: 29%;
  background: linear-gradient(180deg, #ffb86b 0%, #ff7417 100%);
}

.trend-line.cost {
  left: 52%;
  background: linear-gradient(180deg, #fca5a5 0%, #dc2626 100%);
}

.trend-line.agent {
  right: 6%;
  background: linear-gradient(180deg, #c4b5fd 0%, #7c3aed 100%);
}

.trend-item strong {
  color: #d8dfeb;
  font-size: 12px;
}

.trend-item span {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  line-height: 1.25;
}

.trend-item b {
  font-weight: 700;
}

.trend-item b.recharge {
  color: #67e8f9;
}

.trend-item b.consume {
  color: #ffb86b;
}

.trend-item b.cost {
  color: #fca5a5;
}

.trend-item b.agent {
  color: #c4b5fd;
}

.trend-item em {
  color: #22c55e;
  font-style: normal;
  font-size: 12px;
}

.loading-box,
.empty-chart {
  flex: 1;
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8f9bad;
}

.ghost-btn {
  border-color: #314059;
  background: #121a28;
  color: #d6deea;
  border-radius: 6px;
}

.ghost-btn:hover {
  border-color: #ff7417;
  color: #ff9b4a;
  background: #161f2f;
}

@media (max-width: 1200px) {
  .page-hero {
    align-items: stretch;
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
